// Shared visual primitives for the redesigned Superadmin consoles.
// Warm TriSphere palette: forest / terracotta / ochre + ivory surfaces.

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";
import { ReactNode } from "react";

export type Tone = "forest" | "terracotta" | "ochre" | "ink";

export const toneVar = (t: Tone) =>
  t === "terracotta" ? "var(--terracotta)"
  : t === "ochre" ? "var(--ochre)"
  : t === "ink" ? "oklch(0.28 0.02 260)"
  : "var(--forest)";

/* Section hero — gradient title band with eyebrow, headline, meta chips */
export function SectionHero({
  eyebrow, title, subtitle, icon: Icon, tone = "forest", chips = [], accent,
}: {
  eyebrow: string; title: string; subtitle: string; icon: any; tone?: Tone;
  chips?: { label: string; value: string; tone?: Tone }[]; accent?: ReactNode;
}) {
  const gradient = tone === "terracotta"
    ? "linear-gradient(135deg, oklch(0.32 0.075 150) 0%, oklch(0.55 0.15 40) 100%)"
    : tone === "ochre"
    ? "linear-gradient(135deg, oklch(0.42 0.09 155) 0%, oklch(0.72 0.13 75) 100%)"
    : tone === "ink"
    ? "linear-gradient(135deg, oklch(0.22 0.03 260) 0%, oklch(0.38 0.06 220) 100%)"
    : "linear-gradient(135deg, oklch(0.28 0.07 155) 0%, oklch(0.44 0.1 155) 55%, oklch(0.6 0.13 90) 100%)";

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl text-white shadow-card"
      style={{ background: gradient }}
    >
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-30" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.65), transparent 70%)" }} />
      <div aria-hidden className="pointer-events-none absolute -bottom-16 -left-10 h-56 w-56 rounded-full opacity-15" style={{ background: "radial-gradient(circle, rgba(255,255,255,0.9), transparent 70%)" }} />
      <div className="relative grid gap-4 p-6 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/80">
            <span className="grid h-6 w-6 place-items-center rounded-md bg-white/15 backdrop-blur"><Icon className="h-3.5 w-3.5" /></span>
            {eyebrow}
          </div>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl">{title}</h2>
          <p className="mt-1 max-w-2xl text-sm text-white/85">{subtitle}</p>
          {chips.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {chips.map((c) => (
                <div key={c.label} className="rounded-full border border-white/30 bg-white/10 px-3 py-1 text-[11px] backdrop-blur">
                  <span className="text-white/70">{c.label} · </span>
                  <span className="font-semibold">{c.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {accent}
      </div>
    </motion.div>
  );
}

/* Stat tile — richer than KPI, with delta + micro spark */
export function StatTile({
  icon: Icon, label, value, sub, tone = "forest", delta, spark,
}: {
  icon: any; label: string; value: ReactNode; sub?: string; tone?: Tone;
  delta?: { value: string; positive?: boolean };
  spark?: number[];
}) {
  const color = toneVar(tone);
  return (
    <Card className="relative overflow-hidden p-5 shadow-card">
      <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: color }} />
      <div className="flex items-start justify-between">
        <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: `color-mix(in oklab, ${color} 12%, transparent)`, color }}>
          <Icon className="h-4 w-4" />
        </div>
        {delta && (
          <div className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${delta.positive ? "bg-success/15 text-success-foreground" : "bg-[var(--terracotta)]/12 text-[var(--terracotta)]"}`}>
            {delta.positive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {delta.value}
          </div>
        )}
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl leading-tight">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
      {spark && spark.length > 1 && <Sparkline data={spark} color={color} className="mt-3 h-8 w-full" />}
    </Card>
  );
}

/* Sparkline — tiny SVG area chart */
export function Sparkline({ data, color, className }: { data: number[]; color: string; className?: string }) {
  const w = 100, h = 28;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const step = w / (data.length - 1 || 1);
  const pts = data.map((v, i) => [i * step, h - ((v - min) / span) * h] as const);
  const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const area = `${path} L ${w} ${h} L 0 ${h} Z`;
  const id = `sp-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={className}>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Radial gauge — a percentage ring */
export function RingGauge({ value, label, tone = "forest", size = 120 }: { value: number; label?: string; tone?: Tone; size?: number }) {
  const color = toneVar(tone);
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div className="relative inline-flex flex-col items-center">
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="oklch(0.9 0.02 90)" strokeWidth="8" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="8" fill="none"
          strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-display text-2xl leading-none">{Math.round(pct)}%</div>
          {label && <div className="mt-0.5 text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>}
        </div>
      </div>
    </div>
  );
}

/* Bar row — for compact horizontal charts */
export function BarRow({ label, value, max, tone = "forest", right }: { label: ReactNode; value: number; max: number; tone?: Tone; right?: ReactNode }) {
  const color = toneVar(tone);
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm"><div className="truncate">{label}</div><div className="text-muted-foreground">{right}</div></div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, color-mix(in oklab, ${color} 55%, white))` }} />
      </div>
    </div>
  );
}

/* Panel — consistent framed card with header + optional footer */
export function Panel({
  title, subtitle, icon: Icon, badge, right, children, className = "",
}: {
  title: string; subtitle?: string; icon?: any; badge?: ReactNode; right?: ReactNode;
  children: ReactNode; className?: string;
}) {
  return (
    <Card className={`overflow-hidden shadow-card ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 border-b bg-gradient-to-r from-transparent to-[color-mix(in_oklab,var(--forest)_5%,transparent)] p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-display text-lg">
            {Icon && <Icon className="h-4 w-4 text-[var(--forest)]" />}
            <span className="truncate">{title}</span>
            {badge}
          </div>
          {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

export function LiveDot({ tone = "forest" }: { tone?: Tone }) {
  const c = toneVar(tone);
  return <span className="relative inline-flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-50" style={{ background: c }} /><span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: c }} /></span>;
}
